import arrowRight from '@/images/arrow-right-white.svg';

import { useNavigate } from 'react-router-dom';
import { DownloadThemeButton } from './DownloadThemeButton';
import { Account, VersionAccount } from '@/types';
import { generateCertificate, startDownloadCertificate } from '@/services/certificate';
import { twMerge } from 'tailwind-merge';
import { VersionItem } from '@/types';
import { useTranslation } from 'react-i18next';

export function VersionManagement({
    account,
    accountVersions,
}: {
    account: Account;
    accountVersions: VersionAccount[];
}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const currentVersions = accountVersions.filter(version => version.finished === false);

    const finishedVersions = accountVersions.filter(version => version.finished === true);

    const calculateVersionProgress = (version: VersionItem) => {
        const versionStatus = accountVersions.find(status => status.formation_version_id === version._id);

        const totalLessons = version.lessons?.length || 0;
        const finishedLessons = versionStatus?.lesson_statuses
            ? Object.values(versionStatus.lesson_statuses).filter(Boolean).length
            : 0;

        return { totalLessons, finishedLessons };
    };

    return (
        <>
            <div className="mt-8 w-full border-t border-[#E0E3E9]"></div>
            <div className="flex w-full flex-col mt-6">
                <h2 className="font-semibold text-xl text-text-dark">{t('school_ongoing_formations')}</h2>
                <div className="flex flex-row flex-wrap mt-6 gap-8 items-center">
                    {currentVersions.length === 0 && (
                        <p className="text-gray-500">{t('school_no_ongoing_formations')}</p>
                    )}
                    {currentVersions.map((version, index) => {
                        const { totalLessons, finishedLessons } = calculateVersionProgress(version.formationVersion);

                        return (
                            <div
                                key={index}
                                className={twMerge(
                                    version.formation?.theme === 'indigo'
                                        ? 'from-ar-indigo/70 to-ar-indigo shadow-indigo'
                                        : 'from-ar-red/70 to-ar-red shadow-red',
                                    'group/formation relative w-[261px] h-[110px] flex flex-col justify-center pl-5 rounded-xl bg-gradient-to-b cursor-pointer'
                                )}
                            >
                                <a
                                    onClick={() => {
                                        if (!version._id) return;
                                        navigate(
                                            `/formation/${version.formation?._id}/version/${
                                                version.formationVersion._id
                                            }/lesson/${finishedLessons + 1}`
                                        );
                                    }}
                                >
                                    <img
                                        className="absolute right-0 h-full"
                                        src={`/images/deco/profil-formation-card.svg`}
                                    />
                                    <div className="flex flex-row items-center">
                                        <h3 className="font-semibold text-white text-[23px] tracking-tighter">
                                            {t(version.formation?.name ?? '')}
                                        </h3>

                                        <img
                                            className="transition-all duration-300 ease-in-out ml-0 mt-1 opacity-0 group-hover/formation:ml-2 group-hover/formation:opacity-100"
                                            src={arrowRight}
                                            alt="Arrow next"
                                        />
                                    </div>
                                    <p
                                        className="font-semibold text-white text-sm opacity-90 "
                                        dangerouslySetInnerHTML={{ __html: `${t(version.formationVersion.name)}` }}
                                    ></p>

                                    <p className="font-semibold text-white text-sm opacity-90">
                                        {t('school_number_of_finished_lessons', {
                                            nbFinished: finishedLessons,
                                            nbTotal: totalLessons,
                                        })}
                                    </p>
                                </a>
                            </div>
                        );
                    })}
                </div>
            </div>
            <>
                <div className="flex w-full flex-col mt-6">
                    <h2 className="font-semibold text-xl text-text-dark">{t('school_finished_formations')}</h2>
                    <div className="flex flex-row flex-wrap mt-6 gap-8 items-center">
                        {finishedVersions.length === 0 && (
                            <p className="text-gray-500">{t('school_no_finished_formations')}</p>
                        )}
                        {finishedVersions.map((version, index) => (
                            <div key={index}>
                                <a
                                    className={`group/formation relative w-[261px] h-[110px] flex flex-col justify-center pl-5 rounded-xl bg-gradient-to-b ${
                                        version.formation?.theme === 'indigo'
                                            ? 'from-ar-indigo/70 to-ar-indigo shadow-indigo'
                                            : 'from-ar-red/70 to-ar-red shadow-red'
                                    } cursor-pointer`}
                                >
                                    <img
                                        className="absolute right-0 h-full"
                                        src={`/images/deco/profil-formation-card.svg`}
                                    />
                                    <div className="flex flex-row items-center">
                                        <h3 className="font-semibold text-white text-[23px] tracking-tighter">
                                            {t(version.formation?.name ?? '')}
                                        </h3>
                                        <img
                                            className="transition-all duration-300 ease-in-out ml-0 mt-1 opacity-0 group-hover/formation:ml-2 group-hover/formation:opacity-100"
                                            src={arrowRight}
                                            alt="Arrow next"
                                        />
                                    </div>
                                    <p
                                        className="font-semibold text-white text-sm opacity-90"
                                        dangerouslySetInnerHTML={{ __html: `${t(version.formationVersion.name)}` }}
                                    ></p>
                                    <p className="font-semibold text-white text-sm opacity-90">
                                        {t('school_finished_formation')}
                                    </p>
                                </a>
                                <div className="flex flex-col mt-4">
                                    <div className="relative flex flex-row w-fit flex-wrap mt-2 gap-4 items-center">
                                        <DownloadThemeButton
                                            image={`/images/pdf-${version.formation?.theme || 'indigo'}.svg`}
                                            onClick={async () => {
                                                const url = await generateCertificate({
                                                    theme: version.formation?.theme || 'indigo',
                                                    firstname: account.firstname || '',
                                                    last_name: account.lastname || '',
                                                    formationName: t(version.formation?.name ?? ''),
                                                    deliveryDate: version.certificate?.finished_date || '',
                                                    certificate_number: `n°${
                                                        (version.certificate?.certificate_number || 0)
                                                            .toString()
                                                            .padStart(8, '0') || ''
                                                    }`,
                                                });
                                                if (!accountVersions || !account) return;
                                                startDownloadCertificate(
                                                    t(version.formation?.name ?? '') +
                                                        '_' +
                                                        account?.firstname?.toLowerCase() +
                                                        account?.lastname?.toLowerCase() +
                                                        '_certificate.pdf',
                                                    url
                                                );
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="mt-8 w-full border-t border-[#E0E3E9]"></div>
                {/* <div className="flex w-full flex-col mt-6">
                    <h2 className="font-semibold text-xl text-text-dark">{t('school_downloads')}</h2>
                    <div className="flex flex-col mt-4">
                        <h4 className="text-text-grey text-sm font-semibold">{t('school_badges')} :</h4>

                        <div className="relative flex flex-row w-fit flex-wrap mt-2 gap-4 items-center">
                            {['indigo', 'red'].map(theme => (
                                <div key={theme}>
                                    <DownloadThemeButton image={`/images/badge-${theme}.svg`} onClick={() => {}} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div> */}
            </>
        </>
    );
}
